import {
  CFormCheck,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { faPercent, faUserSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format } from "date-fns";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ProfilePictureAvatar } from "../../components/taxi/Profile_picture_avatar";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import { getBadgeClassNameByStatus } from "../../helpers/colors";
import { RideStatus, RideStatusLabels } from "../../helpers/enums";
import {
  formatDateString,
  isNotEmpty,
  paymentMethodHelper,
} from "../../helpers/general";

interface Props {
  riders: Ride[];
  onRiderSelect?: React.Dispatch<BulkDeleteActions>;
  showDriver?: boolean;
  selectedRides?: string[];
  ShowSelectCount?: boolean;
}

const CANCELLED_STATUSES = [
  RideStatus.ADMIN_CANCELLED,
  RideStatus.DRIVER_CANCELLED,
  RideStatus.USER_CANCELLED,
  RideStatus.NO_DRIVER_FOUND,
];

const formatDistance = (meters: number) =>
  meters < 1000
    ? `${Math.round(meters)} m`
    : `${(meters / 1000).toFixed(1)} km`;

/* ─────────────── Small building blocks ─────────────── */

const thStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#777",
  whiteSpace: "nowrap",
  padding: "12px 14px",
  backgroundColor: "#fafafa",
  borderBottom: "1px solid #eee",
};

const tdStyle: React.CSSProperties = {
  padding: "14px",
  verticalAlign: "middle",
  fontSize: 13,
};

function Chip({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "success" | "danger" | "warning";
}) {
  const tones = {
    neutral: { fg: "#555", bg: "#f2f2f2" },
    success: { fg: "#198754", bg: "rgba(25,135,84,0.12)" },
    danger: { fg: "#dc3545", bg: "rgba(220,53,69,0.12)" },
    warning: { fg: "#f0813f", bg: "rgba(240,129,63,0.14)" },
  }[tone];

  return (
    <span
      className="d-inline-flex align-items-center gap-1 rounded-pill"
      style={{
        height: 22,
        padding: "0 9px",
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: "nowrap",
        color: tones.fg,
        backgroundColor: tones.bg,
      }}
    >
      {children}
    </span>
  );
}

/** paid: true = paid, false = unpaid, null = not applicable (hidden) */
function PaidPill({ label, paid }: { label: string; paid: boolean | null }) {
  if (paid === null) return null;
  return (
    <Chip tone={paid ? "success" : "danger"}>
      <span>{paid ? "✓" : "✕"}</span>
      {label}
    </Chip>
  );
}

function PersonCell({
  person,
  href,
  showOnline,
}: {
  person?: any;
  href: string;
  showOnline?: boolean;
}) {
  if (!person) return <span className="text-muted">—</span>;
  return (
    <div className="d-flex align-items-center gap-2" style={{ minWidth: 150 }}>
      <ProfilePictureAvatar
        profilePicture={person.profilePicture}
        fullName={person.fullName}
        isOnline={showOnline ? person.driverProfile?.isOnline : undefined}
      />
      <Link to={href} className="fw-semibold text-capitalize">
        {person.fullName}
      </Link>
    </div>
  );
}

function RouteCell({
  from,
  to,
  distance,
  duration,
}: {
  from: string;
  to: string;
  distance?: number;
  duration?: number;
}) {
  const dot = (color: string): React.CSSProperties => ({
    width: 9,
    height: 9,
    borderRadius: "50%",
    backgroundColor: color,
    flexShrink: 0,
  });

  return (
    <div style={{ minWidth: 260, maxWidth: 340 }}>
      <div className="d-flex gap-2">
        <div
          className="d-flex flex-column align-items-center"
          style={{ width: 10, paddingTop: 5, paddingBottom: 5 }}
        >
          <span style={dot("#f0813f")} />
          <span
            style={{
              flex: 1,
              width: 2,
              margin: "3px 0",
              backgroundColor: "#e2e2e2",
            }}
          />
          <span style={dot("#22AAFF")} />
        </div>
        <div className="d-flex flex-column gap-2" style={{ minWidth: 0 }}>
          <span
            className="text-truncate"
            title={from}
            style={{ maxWidth: 310 }}
          >
            {from}
          </span>
          <span className="text-truncate" title={to} style={{ maxWidth: 310 }}>
            {to}
          </span>
        </div>
      </div>
      <div className="d-flex gap-1 mt-2 ms-3 ps-1">
        {distance != null && <Chip>{formatDistance(distance)}</Chip>}
        {duration ? <Chip>{Math.floor(duration / 60)} min</Chip> : null}
      </div>
    </div>
  );
}

/* ─────────────── Row ─────────────── */

interface RowProps {
  ride: Ride;
  showDriver: boolean;
  isChecked: boolean;
  onToggle?: (checked: boolean) => void;
}

function RideRow({ ride, showDriver, isChecked, onToggle }: RowProps) {
  const coupon = ride?.priceCalculation?.coupon;
  const isCouponApplied = Boolean(coupon?.isCouponApplied);
  const discountAmount = isCouponApplied
    ? +ride?.originalFare - coupon?.fareAfterCoupon
    : 0;

  const driverPaid = Boolean(ride?.metaData?.driverPayment?.paid);
  const couponPaid = Boolean(ride?.metaData?.couponDiscount?.paid);
  const balancePaid = Boolean(ride?.metaData?.userBalancePayment?.paid);

  const disableCheckbox =
    !ride.metaData?.validRide ||
    (driverPaid && (isCouponApplied ? couponPaid : true)) ||
    ride.status !== RideStatus.COMPLETED;

  const validity =
    ride.metaData?.validRide === true
      ? "valid"
      : ride.metaData?.validRide === false ||
          CANCELLED_STATUSES.includes(ride.status as any)
        ? "invalid"
        : "pending";

  return (
    <CTableRow
      style={{
        backgroundColor: isChecked ? "rgba(240,129,63,0.06)" : undefined,
        boxShadow: isChecked ? `inset 3px 0 0 ${colors.primary}` : undefined,
      }}
    >
      {/* Checkbox */}
      <CTableDataCell style={{ ...tdStyle, width: 44 }}>
        {!showDriver && (
          <CFormCheck
            checked={isChecked}
            onChange={(e) => onToggle?.(e.target.checked)}
            disabled={disableCheckbox}
          />
        )}
      </CTableDataCell>

      {/* Ride: reference + date */}
      <CTableDataCell style={tdStyle}>
        <Link
          to={`/rides/${ride.id}/ridedetails`}
          state={{ ride }}
          className="fw-bold"
        >
          #{ride.reference}
        </Link>
        <div className="text-muted mt-1" style={{ fontSize: 12 }}>
          {formatDateString(ride.createdAt)} ·{" "}
          {format(new Date(ride.createdAt), "HH:mm")}
        </div>
        {isCouponApplied && (
          <div className="mt-1">
            <Chip tone="warning">
              -{coupon?.couponPercentage}
              <FontAwesomeIcon icon={faPercent} size="xs" />
              coupon
            </Chip>
          </div>
        )}
      </CTableDataCell>

      {/* Driver */}
      {showDriver && (
        <CTableDataCell style={tdStyle}>
          <PersonCell
            person={ride.driver}
            href={`/drivers/${ride.driver?.id}`}
            showOnline
          />
        </CTableDataCell>
      )}

      {/* Client */}
      <CTableDataCell style={tdStyle}>
        {!isNotEmpty(ride.user) ? (
          <span className="text-muted" style={{ fontSize: 12 }}>
            <FontAwesomeIcon icon={faUserSlash} className="me-1" />
            Compte supprimé
          </span>
        ) : (
          <PersonCell person={ride.user} href={`/users/${ride.user.id}`} />
        )}
      </CTableDataCell>

      {/* Route */}
      <CTableDataCell style={tdStyle}>
        <RouteCell
          from={ride.pickupLocationName}
          to={ride.destinationLocationName}
          distance={ride.distance}
          duration={ride.estimatedDuration}
        />
      </CTableDataCell>

      {/* Status + validity */}
      <CTableDataCell style={tdStyle}>
        <div className="d-flex flex-column align-items-start gap-2">
          <span
            className={
              ride.status === RideStatus.WAITING_PICKUP
                ? "badge-primary"
                : getBadgeClassNameByStatus(ride.status)
            }
          >
            {RideStatusLabels[ride.status as keyof typeof RideStatusLabels]}
          </span>
          {validity === "valid" && <Chip tone="success">✓ Valide</Chip>}
          {validity === "invalid" && <Chip tone="danger">✕ Non valide</Chip>}
          {validity === "pending" && <Chip>En attente</Chip>}
        </div>
      </CTableDataCell>

      {/* Payment */}
      <CTableDataCell style={tdStyle}>
        <div style={{ minWidth: 120 }}>
          <div className="d-flex align-items-baseline gap-2">
            <span className="fw-bold" style={{ fontSize: 15 }}>
              {ride.fare}
              <span
                className="text-muted fw-normal ms-1"
                style={{ fontSize: 11 }}
              >
                MAD
              </span>
            </span>
            {isCouponApplied && (
              <span
                className="text-muted"
                style={{ fontSize: 12, textDecoration: "line-through" }}
              >
                {ride.originalFare}
              </span>
            )}
          </div>
          {isCouponApplied && (
            <div className="text-success" style={{ fontSize: 12 }}>
              −{discountAmount} MAD
            </div>
          )}
          <div className="text-muted mt-1" style={{ fontSize: 12 }}>
            {paymentMethodHelper(ride?.paymentMethod)}
          </div>
        </div>
      </CTableDataCell>

      {/* Settlements */}
      <CTableDataCell style={tdStyle}>
        <div className="d-flex flex-column align-items-start gap-1">
          <PaidPill label="Bonus" paid={driverPaid} />
          <PaidPill label="Coupon" paid={isCouponApplied ? couponPaid : null} />
          <PaidPill label="Solde" paid={balancePaid} />
        </div>
      </CTableDataCell>
    </CTableRow>
  );
}

/* ─────────────── Table ─────────────── */

export default function RidersTable(props: Props) {
  const {
    riders,
    onRiderSelect,
    showDriver = true,
    selectedRides = [],
    ShowSelectCount = false,
  } = props;
  const [selectCount, setSelectCount] = useState("10");

  const selectableRides = riders.filter(
    (ride) => ride.metaData?.validRide && ride.status === RideStatus.COMPLETED,
  );

  const allSelected =
    selectableRides.length > 0 &&
    selectableRides.every((r) => selectedRides.includes(r.id));
  const someSelected =
    selectableRides.some((r) => selectedRides.includes(r.id)) && !allSelected;

  const toggleRide = (id: string, checked: boolean) =>
    onRiderSelect?.({
      type: checked ? DELETE_ACTIONS.ADD : DELETE_ACTIONS.REMOVE,
      id,
    });

  const handleSelectAll = (checked: boolean) =>
    selectableRides.forEach((r) => toggleRide(r.id, checked));

  const handleSelectCount = () => {
    if (!onRiderSelect) return;
    selectedRides.forEach((id) => toggleRide(id, false));

    const count = Number(selectCount);
    if (!count || count <= 0) return;

    // Earliest rides first
    [...selectableRides]
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      )
      .slice(0, count)
      .forEach((r) => toggleRide(r.id, true));
  };

  return (
    <div className="mt-3">
      {ShowSelectCount && (
        <div
          className="d-flex align-items-center gap-2 mb-3 mx-3 p-2 rounded-pill"
          style={{
            backgroundColor: "#fafafa",
            border: "1px solid #eee",
            width: "fit-content",
          }}
        >
          <input
            type="number"
            min={1}
            max={selectableRides.length}
            value={selectCount}
            onChange={(e) => {
              if (e.target.value === "" || /^\d+$/.test(e.target.value))
                setSelectCount(e.target.value);
            }}
            className="form-control rounded-pill px-3"
            style={{ width: 90, height: 38, fontSize: 13 }}
          />
          <button
            type="button"
            className="btn btn-primary text-white rounded-pill px-3"
            style={{ height: 38, fontSize: 13 }}
            onClick={handleSelectCount}
          >
            {Number(selectCount) > 0
              ? `Sélectionner les ${selectCount} premiers`
              : "Réinitialiser la sélection"}
          </button>
        </div>
      )}

      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell style={{ ...thStyle, width: 44 }}>
              {!showDriver && (
                <div className="d-flex align-items-center gap-2">
                  <CFormCheck
                    checked={allSelected}
                    indeterminate={someSelected}
                    disabled={selectableRides.length === 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                  {selectedRides.length > 0 && (
                    <span
                      className="text-white rounded-pill bg-primary px-2"
                      style={{ fontSize: 11 }}
                    >
                      {selectedRides.length}
                    </span>
                  )}
                </div>
              )}
            </CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Trajet</CTableHeaderCell>
            {showDriver && (
              <CTableHeaderCell style={thStyle}>Chauffeur</CTableHeaderCell>
            )}
            <CTableHeaderCell style={thStyle}>Client</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Itinéraire</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Statut</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Paiement</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Règlements</CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {riders?.map((ride) => (
            <RideRow
              key={ride.id}
              ride={ride}
              showDriver={showDriver}
              isChecked={selectedRides.includes(ride.id)}
              onToggle={(checked) => toggleRide(ride.id, checked)}
            />
          ))}
        </CTableBody>
      </CTable>
    </div>
  );
}
